import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Purchases } from "@revenuecat/purchases-capacitor";
import { getRevenueCatApiKey } from "@/config/revenuecat";
import { supabase } from "@/integrations/supabase/client";

interface RevenueCatContextType {
  isConfigured: boolean;
  isConfiguring: boolean;
  error: string | null;
}

const RevenueCatContext = createContext<RevenueCatContextType>({
  isConfigured: false,
  isConfiguring: false,
  error: null,
});

export const useRevenueCat = () => useContext(RevenueCatContext);

export const RevenueCatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initStartedRef = useRef(false);

  useEffect(() => {
    const initializeRevenueCat = async () => {
      // Prevent duplicate initialization (e.g. React StrictMode in dev)
      if (initStartedRef.current) return;
      initStartedRef.current = true;

      if (!Capacitor.isNativePlatform()) {
        console.log("Skipping RevenueCat initialization (not a native platform)");
        return;
      }

      const apiKey = getRevenueCatApiKey();

      if (!apiKey) {
        const errorMsg =
          "RevenueCat not configured: missing SDK key for this platform. (Check src/config/revenuecat.ts)";
        console.error(errorMsg);
        setIsConfigured(false);
        setError(errorMsg);
        return;
      }

      setIsConfiguring(true);

      try {
        await Purchases.configure({ apiKey });

        // Extra guard: ensure the plugin believes it's configured (some environments can silently no-op).
        const rc = await (Purchases as any).isConfigured?.().catch(() => null);
        if (rc && rc.isConfigured === false) {
          throw new Error("RevenueCat configure did not complete");
        }

        // ── Link RevenueCat to the Supabase user ID ──
        // This is critical: it ensures purchases made on iOS are attributed
        // to the correct Supabase user so webhooks can update subscription
        // status across all platforms (web, Android, iOS).
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user?.id) {
            await Purchases.logIn({ appUserID: session.user.id });
            console.log("RevenueCat logged in with Supabase user ID:", session.user.id);

            // Set $email attribute so it's visible in RevenueCat dashboard
            if (session.user.email) {
              await Purchases.setEmail({ email: session.user.email });
              console.log("RevenueCat $email set:", session.user.email);
            }
          } else {
            console.warn("RevenueCat initialized but no Supabase session found — purchases will use an anonymous ID");
          }
        } catch (loginErr: any) {
          // Non-fatal: SDK is configured, just not linked to a user yet
          console.warn("RevenueCat logIn failed (non-fatal):", loginErr?.message);
        }

        console.log("RevenueCat initialized successfully");
        setIsConfigured(true);
        setError(null);
      } catch (err: any) {
        console.error("Failed to initialize RevenueCat:", err);
        setIsConfigured(false);
        setError(err?.message || "Failed to initialize RevenueCat");
      } finally {
        setIsConfiguring(false);
      }
    };

    void initializeRevenueCat();

    // ── Re-login when auth state changes (sign-in / sign-out) ──
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!Capacitor.isNativePlatform()) return;

      try {
        const configured = await (Purchases as any).isConfigured?.().catch(() => null);
        if (!configured?.isConfigured) return;

        if (session?.user?.id && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          await Purchases.logIn({ appUserID: session.user.id });
          if (session.user.email) {
            await Purchases.setEmail({ email: session.user.email });
          }
          console.log("RevenueCat re-linked to Supabase user:", session.user.id);
        } else if (event === 'SIGNED_OUT') {
          await Purchases.logOut();
          console.log("RevenueCat logged out (anonymous mode)");
        }
      } catch (e: any) {
        console.warn("RevenueCat auth sync error (non-fatal):", e?.message);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <RevenueCatContext.Provider value={{ isConfigured, isConfiguring, error }}>
      {children}
    </RevenueCatContext.Provider>
  );
};
